import Layout from "../components/Layout"
import Stack from "@mui/material/Stack"
import { Grid2 as Grid } from "@mui/material"
import Box from "@mui/material/Box"
import useModalTrigger from "magik-react-hooks/useModalTrigger"
import {
  useProduttore,
  useProduttoreUpdate,
  useProduttoreDelete,
  useProduttoreSetAllegati,
  useProduttoreHistory,
} from "../hooks/produttori"
import { useParams, useNavigate } from "react-router-dom"
import ProduttoreForm from "../components/forms/ProduttoreForm"
import AllegatiForm from "../components/forms/AllegatiForm"
import DialogForm from "../components/DialogForm"
import { useSnackbarMessage } from "../hooks/snackbar"
import { DataField, DataFieldLabel, DataFieldLink, DataRow } from "../components/DataField"
import { SectionTitle } from "../components/SectionTitle"
import { PageTitle } from "../components/PageTitle"
import { useTranslation } from "react-i18next"
import RecordInfo from "../components/RecordInfo"
import DeleteBar from "../components/DeleteBar"
import DialogConfirm from "../components/DialogConfirm"
import { Produttore, WithAllegati } from "../types"
import AllegatiGrid from "../components/AllegatiGrid"
import { useDesiredHeight } from "../hooks/measuring"
import { joinNomeCognome } from "../utils/text"
import useUserCanWriteAndDelete from "../hooks/permissions"
import { SwitchAccount } from "@mui/icons-material"
import { useIsMobile } from "../hooks/mobile"

export default function ProduttorePage() {
  const { id } = useParams()
  const { t, i18n } = useTranslation()
  const { data: produttore, refetch } = useProduttore(parseInt(id as string))
  const userCanWriteAndDelete = useUserCanWriteAndDelete()
  const isMobile = useIsMobile()

  const [updateModalState, updateModalActions] = useModalTrigger()
  const [updateModalStateAllegati, updateModalActionsAllegati] = useModalTrigger()
  const [deleteModalState, deleteModalActions] = useModalTrigger()

  const { mutateAsync: update } = useProduttoreUpdate()
  const { mutateAsync: remove } = useProduttoreDelete()
  const { mutateAsync: setAllegati } = useProduttoreSetAllegati(id as string)

  const showMessage = useSnackbarMessage()
  const navigate = useNavigate()

  const historyBag = useProduttoreHistory(id as string)
  const { ref } = useDesiredHeight(24)
  const lang = i18n.language

  return (
    <Layout>
      <Stack sx={{ height: "100%", overflow: "hidden" }}>
        <PageTitle
          padding
          border
          title={joinNomeCognome(produttore?.nome, produttore?.cognome) || ""}
          breadcrumbs={[
            {
              label: t("general.produttori"),
              iconType: SwitchAccount,
              to: `/${lang}/produttori`,
            },
            {
              label: joinNomeCognome(produttore?.nome, produttore?.cognome),
            },
          ]}
        />
        <Grid
          container
          spacing={2}
          sx={{
            overflow: "auto",
            height: "100%",
            p: 3,
            pt: 2,
          }}
        >
          <Grid
            size={{
              xs: 12,
              xl: 12,
            }}
          >
            <Stack
              flexGrow={1}
              sx={{
                height: "fit-content",
                position: "relative",
                bgcolor: "white",
                boxShadow: 1,
              }}
            >
              <SectionTitle
                first
                title={t("sections.identificazione")}
                onEdit={() => {
                  updateModalActions.open()
                }}
              />
              <Box px={3} pb={2}>
                <DataRow>
                  <DataField
                    label={t("produttore.cognome_nome")}
                    value={joinNomeCognome(produttore?.nome, produttore?.cognome)}
                    size={4}
                  />
                  <DataField label={t("produttore.tipologia")} value={produttore?.tipologia} size={4} />
                  <DataField label={t("produttore.segnatura_base")} value={produttore?.segnatura_base} size={4} />
                </DataRow>

                <DataRow>
                  <DataField
                    label={t("produttore.funzioni")}
                    value={(produttore?.funzioni_data ?? [])?.map((f) => f.termine)}
                    size={8}
                  />
                </DataRow>
                <DataRow>
                  <DataField label={t("produttore.biografia")} value={produttore?.biografia} size={{ xs: 12, xl: 8 }} />
                </DataRow>
                <DataRow last>
                  <DataField label={t("produttore.note")} value={produttore?.note} size={8} />
                </DataRow>

                <DataRow last>
                  <Grid size={12}>
                    <DataFieldLabel label={t("general.fondi")} />
                    <div>
                      {produttore?.fondi_data?.map((fondo, index) => (
                        <span key={fondo.id}>
                          <DataFieldLink display="inline-flex" link={`/fondi/${fondo.id}`} key={fondo.id} value={fondo.segnatura} />
                          {index < (produttore.fondi_data || []).length - 1 && ", "}
                        </span>
                      ))}
                    </div>
                  </Grid>
                </DataRow>
              </Box>
              <SectionTitle
                first
                title={t("sections.allegati")}
                onEdit={() => {
                  updateModalActionsAllegati.open()
                }}
              />
              <Box px={3} py={2}>
                <AllegatiGrid allegati_data={produttore?.allegati_data || []} />
              </Box>
            </Stack>
          </Grid>
          <Grid
            size={{
              xs: 12,
              xl: 12,
            }}
          >
            <Stack
              ref={ref}
              sx={{
                bgcolor: "grey.200",
                borderRadius: 1,
                boxShadow: 1,
              }}
            >
              <SectionTitle bgcolor="grey.700" info />
              <Stack sx={{ p: 2 }} flexGrow={1}>
                <RecordInfo
                  first_update={produttore?.first_update}
                  last_update={produttore?.last_update}
                  historyBag={historyBag as any}
                />
              </Stack>
              {userCanWriteAndDelete && !isMobile && produttore?.can_delete && (
                <DeleteBar title={t("produttore.cta_elimina")} onDelete={() => deleteModalActions.open(produttore)} />
              )}
            </Stack>
          </Grid>
        </Grid>
      </Stack>

      <DialogForm<Produttore>
        open={updateModalState.isOpen}
        handleClose={updateModalActions.close}
        FormComponent={ProduttoreForm}
        hasDoubleLanguage
        title={t("produttore.cta_modifica")}
        save={(values) => {
          return update(values).then(() => {
            updateModalActions.close()
            refetch()
            showMessage(t("produttore.esito_modifica_ok"))
          })
        }}
        item={produttore}
        hasCancel
      />

      <DialogForm<WithAllegati>
        open={updateModalStateAllegati.isOpen}
        handleClose={updateModalActionsAllegati.close}
        FormComponent={AllegatiForm}
        title={t("general.cta_modifica_allegati")}
        save={(values) => {
          const allegatiData = (values.allegati_data || []).map((a) => {
            return {
              id: a.id,
              note: a.note,
              attachment: typeof a.attachment === "string" ? undefined : a.attachment,
            }
          })
          return setAllegati({ allegati: allegatiData }).then(() => {
            updateModalActionsAllegati.close()
            refetch()
            showMessage(t("produttore.esito_modifica_ok"))
          })
        }}
        item={{ allegati_data: produttore?.allegati_data }}
        hasCancel
      />

      <DialogConfirm
        open={deleteModalState.isOpen}
        handleClose={deleteModalActions.close}
        title={t("produttore.cta_elimina")}
        subtitle={
          <span>
            {t("produttore.cta_subtitle_elimina")} <b>"{deleteModalState.value?.nome}"</b>&nbsp;?
          </span>
        }
        textConfirm={t("general.elimina")}
        onConfirm={() => {
          return remove(deleteModalState.value.id).then(() => {
            deleteModalActions.close()
            showMessage(t("produttore.esito_elimina_ok"))
            navigate(`/${i18n.language}/produttori`)
          }).catch(() => {
            deleteModalActions.close()
            showMessage(t("general.esito_elimina_errore"), "error")
          })
        }}
      />
    </Layout>
  )
}
